/**
 * WHEN TWO REAL THINGS WANT THE SAME SPACE (spec §61C).
 *
 * A renovation is planned against a survey that is always slightly wrong.
 * The user discovers a window nobody drew, adds it to Existing, and the plan
 * already has cabinets on that wall. Berrier Reno must not overwrite the
 * plan, must not ignore the discovery, and — this is the part that is easy
 * to get wrong — must not shout "conflict" at every object that happens to
 * be on the same wall.
 *
 * A conflict is GEOMETRIC and SEMANTIC:
 *
 *   • their plan footprints actually overlap, and
 *   • the vertical bands they occupy actually overlap, and
 *   • they are the kinds of things that cannot share space.
 *
 * A wall cabinet hung at 54" under a window whose sill is at 60" is not a
 * conflict; it is how kitchens are built. The same cabinet run through the
 * middle of that window is.
 *
 * AND WHERE THE MODEL DOES NOT KNOW, IT SAYS SO. An object with no stated
 * height cannot be proven to clash, so it is reported as something to look
 * at — never as a certainty. Manufactured certainty is worse than silence.
 *
 * Pure: no React, no DOM, no SQL, no database types.
 */
import type { PlanModel, PlanObject, Wall, WallOpening, Point } from "./model";
import { activeObjects, activeOpenings, activeWalls, nodeOf } from "./model";
import { objectFootprint } from "./objects";
import { PU_PER_INCH } from "./units";

/** What kind of thing this is, for the purpose of sharing space. */
export type ConflictClass = "cabinet" | "fixture" | "appliance" | "structure" | "ignore";

export interface ConflictObjectInfo {
  /** Display name, for the sentence: "Cabinet intersects existing window". */
  label: string;
  klass: ConflictClass;
  /** Inches from the finished floor; null = nobody has said. */
  bottomInches: number | null;
  topInches: number | null;
  /** True when this object was inherited from Existing rather than planned. */
  inherited?: boolean;
}

export type ConflictCertainty = "definite" | "review";

export interface DesignConflict {
  /** Stable across recomputation, so a dismissal stays dismissed. */
  key: string;
  certainty: ConflictCertainty;
  objectId: string;
  otherKind: "opening" | "object" | "wall";
  otherId: string;
  /** One sentence, in plain words: what overlaps what. */
  summary: string;
  /** Why it is only worth a look, when that is the case. */
  reason: string | null;
  /**
   * The inherited opening this conflict is about, when the resolution
   * "remove it in this plan" is available. Never Existing's own copy.
   */
  openingId: string | null;
}

export interface ConflictInput {
  model: PlanModel;
  /** Per-object facts the geometry engine cannot know by itself. */
  info: ReadonlyMap<string, ConflictObjectInfo>;
  /** Limit the answer to these objects (e.g. the ones just affected). */
  onlyObjectIds?: ReadonlySet<string>;
  /** Walls added by this plan — a new wall across an old window is a clash. */
  addedWallIds?: ReadonlySet<string>;
}

/** Overlap smaller than this is drawing noise, not a collision: half an inch. */
const OVERLAP_TOLERANCE_PU = Math.round(PU_PER_INCH / 2);

/**
 * How far in front of an opening still counts as being in the way.
 *
 * An opening's own footprint is INSIDE the wall, and cabinets are installed
 * AGAINST walls — so a cabinet standing squarely in front of a window shares
 * no plan area with it, only an edge. Testing the wall band alone therefore
 * finds nothing, which is what the hosted acceptance run demonstrated. The
 * zone that matters is the opening plus the space immediately in front of
 * it, on both sides, which is where anything blocking it would stand.
 *
 * Six inches: enough to catch what is fitted against the wall, small enough
 * that a kitchen island a foot away is not accused of being in the way.
 */
const APPROACH_PU = 6 * PU_PER_INCH;

const puToInches = (pu: number): number => pu / PU_PER_INCH;

/* ------------------------------------------------------------------ *
 * Geometry
 * ------------------------------------------------------------------ */

/**
 * The zone an opening occupies in plan: its width along the wall, and the
 * wall's thickness plus the approach in front of it (see APPROACH_PU).
 */
export function openingFootprint(model: PlanModel, opening: WallOpening, approachPu = APPROACH_PU): Point[] | null {
  const wall = model.walls.get(opening.wallId);
  if (!wall || wall.archivedAt !== null) return null;
  return spanFootprint(model, wall, opening.offsetFromStart, opening.width, approachPu);
}

/** The rectangle a whole wall occupies in plan. */
export function wallFootprint(model: PlanModel, wall: Wall): Point[] | null {
  const a = model.nodes.get(wall.startNodeId);
  const b = model.nodes.get(wall.endNodeId);
  if (!a || !b) return null;
  const length = Math.hypot(b.x - a.x, b.y - a.y);
  if (length === 0) return null;
  return spanFootprint(model, wall, 0, length);
}

function spanFootprint(model: PlanModel, wall: Wall, offset: number, length: number, approachPu = 0): Point[] | null {
  const a = nodeOf(model, wall.startNodeId);
  const b = nodeOf(model, wall.endNodeId);
  const full = Math.hypot(b.x - a.x, b.y - a.y);
  if (full === 0) return null;
  const ux = (b.x - a.x) / full;
  const uy = (b.y - a.y) / full;
  const nx = -uy;
  const ny = ux;
  const half = Math.max(wall.thickness, PU_PER_INCH) / 2 + approachPu;
  const x0 = a.x + ux * offset;
  const y0 = a.y + uy * offset;
  const x1 = x0 + ux * length;
  const y1 = y0 + uy * length;
  return [
    { x: Math.round(x0 + nx * half), y: Math.round(y0 + ny * half) },
    { x: Math.round(x1 + nx * half), y: Math.round(y1 + ny * half) },
    { x: Math.round(x1 - nx * half), y: Math.round(y1 - ny * half) },
    { x: Math.round(x0 - nx * half), y: Math.round(y0 - ny * half) },
  ];
}

/**
 * Do two convex footprints share area? Separating-axis test with a tolerance,
 * so touching is not colliding — cabinets are installed against walls.
 */
export function footprintsOverlap(a: readonly Point[], b: readonly Point[], tolerancePu = OVERLAP_TOLERANCE_PU): boolean {
  if (a.length < 3 || b.length < 3) return false;
  for (const polygon of [a, b]) {
    for (let i = 0; i < polygon.length; i += 1) {
      const p = polygon[i];
      const q = polygon[(i + 1) % polygon.length];
      const axis = { x: -(q.y - p.y), y: q.x - p.x };
      const len = Math.hypot(axis.x, axis.y);
      if (len === 0) continue;
      const ux = axis.x / len;
      const uy = axis.y / len;
      let minA = Infinity;
      let maxA = -Infinity;
      for (const pt of a) {
        const t = pt.x * ux + pt.y * uy;
        minA = Math.min(minA, t);
        maxA = Math.max(maxA, t);
      }
      let minB = Infinity;
      let maxB = -Infinity;
      for (const pt of b) {
        const t = pt.x * ux + pt.y * uy;
        minB = Math.min(minB, t);
        maxB = Math.max(maxB, t);
      }
      if (maxA - tolerancePu <= minB || maxB - tolerancePu <= minA) return false;
    }
  }
  return true;
}

/**
 * Do two vertical bands share space? `null` means the model does not know —
 * which is NOT the same answer as "no", and must stay different all the way
 * to the user.
 */
export function bandsIntersect(
  a: { bottomInches: number | null; topInches: number | null },
  b: { bottomInches: number | null; topInches: number | null },
): boolean | null {
  if (a.bottomInches === null || b.bottomInches === null || a.topInches === null || b.topInches === null) return null;
  const clearance = 0.5; // half an inch: a cabinet touching a sill is not inside it
  return a.bottomInches < b.topInches - clearance && b.bottomInches < a.topInches - clearance;
}

/** The vertical band an opening occupies: its sill, and its own height above it. */
export function openingBand(opening: WallOpening): { bottomInches: number; topInches: number } {
  const bottom = puToInches(opening.sillHeight);
  return { bottomInches: bottom, topInches: bottom + puToInches(opening.height) };
}

/* ------------------------------------------------------------------ *
 * Detection
 * ------------------------------------------------------------------ */

const CAN_CLASH: ReadonlySet<ConflictClass> = new Set<ConflictClass>(["cabinet", "fixture", "appliance", "structure"]);

export function detectConflicts(input: ConflictInput): DesignConflict[] {
  const { model, info } = input;
  const conflicts: DesignConflict[] = [];
  const objects = activeObjects(model).filter((o) => {
    if (input.onlyObjectIds && !input.onlyObjectIds.has(o.id)) return false;
    const meta = info.get(o.id);
    return !!meta && CAN_CLASH.has(meta.klass);
  });
  const openings = activeOpenings(model);

  for (const object of objects) {
    const meta = info.get(object.id)!;
    const footprint = objectFootprint(model, object);

    // ---- against openings: the case the beta actually hit ----
    for (const opening of openings) {
      // An opening in the wall this object is FIXED TO is the normal case
      // for a wall-hosted object, and is handled by elevation like any
      // other; nothing special is assumed here.
      const other = openingFootprint(model, opening);
      if (!other || !footprintsOverlap(footprint, other)) continue;
      const vertical = bandsIntersect(meta, openingBand(opening));
      const noun = opening.kind === "door" ? "door" : opening.kind === "window" ? "window" : "opening";
      if (vertical === true) {
        conflicts.push({
          key: `object-opening:${object.id}:${opening.id}`,
          certainty: "definite",
          objectId: object.id,
          otherKind: "opening",
          otherId: opening.id,
          summary: `${meta.label} intersects the ${noun}.`,
          reason: null,
          openingId: opening.id,
        });
      } else if (vertical === null) {
        conflicts.push({
          key: `object-opening:${object.id}:${opening.id}`,
          certainty: "review",
          objectId: object.id,
          otherKind: "opening",
          otherId: opening.id,
          summary: `${meta.label} sits where the ${noun} is.`,
          reason: heightUnknownReason(meta),
          openingId: opening.id,
        });
      }
      // vertical === false: a high window above a cabinet run. Not a
      // conflict, and saying nothing is the correct behaviour.
    }

    // ---- against other objects ----
    for (const other of objects) {
      if (other.id <= object.id) continue; // each pair once
      const otherMeta = info.get(other.id)!;
      if (!footprintsOverlap(footprint, objectFootprint(model, other))) continue;
      const vertical = bandsIntersect(meta, otherMeta);
      if (vertical === false) continue;
      conflicts.push({
        key: `object-object:${object.id}:${other.id}`,
        certainty: vertical === true ? "definite" : "review",
        objectId: object.id,
        otherKind: "object",
        otherId: other.id,
        summary: `${meta.label} overlaps the ${otherMeta.label.toLowerCase()}.`,
        reason: vertical === null ? heightUnknownReason(meta, otherMeta) : null,
        openingId: null,
      });
    }

    // ---- structure against walls: a staircase through a wall ----
    if (meta.klass === "structure") {
      for (const wall of activeWalls(model)) {
        if (wall.kind === "boundary") continue;
        if (object.hostWallId === wall.id) continue;
        const other = wallFootprint(model, wall);
        if (!other || !footprintsOverlap(footprint, other)) continue;
        conflicts.push({
          key: `object-wall:${object.id}:${wall.id}`,
          certainty: "definite",
          objectId: object.id,
          otherKind: "wall",
          otherId: wall.id,
          summary: `${meta.label} runs through a wall.`,
          reason: null,
          openingId: null,
        });
      }
    }
  }

  // ---- a NEW wall across an inherited opening ----
  for (const wallId of input.addedWallIds ?? []) {
    const wall = model.walls.get(wallId);
    if (!wall || wall.archivedAt !== null || wall.kind === "boundary") continue;
    const footprint = wallFootprint(model, wall);
    if (!footprint) continue;
    for (const opening of openings) {
      if (opening.wallId === wall.id) continue;
      const other = openingFootprint(model, opening);
      if (!other || !footprintsOverlap(footprint, other)) continue;
      const noun = opening.kind === "door" ? "door" : "window";
      conflicts.push({
        key: `wall-opening:${wall.id}:${opening.id}`,
        certainty: "definite",
        objectId: "",
        otherKind: "opening",
        otherId: opening.id,
        summary: `A new wall crosses the ${noun}.`,
        reason: null,
        openingId: opening.id,
      });
    }
  }

  return conflicts.sort((a, b) => a.key.localeCompare(b.key));
}

function heightUnknownReason(a: ConflictObjectInfo, b?: ConflictObjectInfo): string {
  const unknown: string[] = [];
  if (a.bottomInches === null || a.topInches === null) unknown.push(a.label.toLowerCase());
  if (b && (b.bottomInches === null || b.topInches === null)) unknown.push(b.label.toLowerCase());
  const what = unknown.length > 0 ? unknown.join(" and the ") : "one of them";
  return `They share the same floor space. Nobody has said how tall the ${what} is, so whether they actually clash is worth a look.`;
}

/** Only the ones we are sure about — what a badge should count. */
export function definiteConflicts(conflicts: readonly DesignConflict[]): DesignConflict[] {
  return conflicts.filter((c) => c.certainty === "definite");
}

export type { PlanObject };
